import {
  PALETTE,
  chosenOptionIds,
  colourOfTrack,
  fromNumericKind,
  matchTrackKind,
  questionFor,
  toNumericKind,
  type BooleanKind,
  type EnumKind,
  type MultiKind,
  type NumericKind,
  type PaletteColour,
  type RemovalPlan,
  type ScaleConfig,
  type Track,
  type TrackKind,
  type TrackKindName,
  type TrackValue,
  type ValidationError,
  type ValueWord,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ExamplePackOffer } from '../../components/ExamplePackOffer.js'
import { TextArea, TextField } from '../../components/Field.js'
import {
  ActionBar,
  ActionButton,
  Page,
  Section,
  SubHeader,
  Swatch,
} from '../../components/Layout.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { useServices } from '../../services/ServicesProvider.js'
import { UnsavedWorkGuard, useDiscardGuard } from '../../shell/DiscardGuard.js'
import { EnumKindEditor } from './EnumKindEditor.js'
import { MultiKindEditor } from './MultiKindEditor.js'
import { NumericKindEditor } from './NumericKindEditor.js'
import { RemoveTrackDialog } from './RemoveTrackDialog.js'
import { ValueWordsEditor } from './ValueWordsEditor.js'
import styles from './TrackEditor.module.css'

const DEFAULT_NUMERIC: NumericKind = {
  kind: 'numeric',
  min: 1,
  max: 10,
  step: 1,
  decimals: 0,
}
const DEFAULT_BOOLEAN: BooleanKind = {
  kind: 'boolean',
  trueLabel: 'Yes',
  falseLabel: 'No',
}

const KIND_NAMES: {
  readonly id: TrackKindName
  readonly label: string
  readonly hint: string
}[] = [
  {
    id: 'numeric',
    label: 'A number',
    hint: 'A scale or an amount, like 1 to 10 or minutes',
  },
  {
    id: 'enum',
    label: 'One of a set',
    hint: 'Pick one option, with or without an order',
  },
  { id: 'multi', label: 'Several of a set', hint: 'Pick any that apply' },
  { id: 'boolean', label: 'Yes or no', hint: 'With your own words for each' },
  { id: 'marker', label: 'A marker', hint: 'Only that it happened' },
]

/** A palette name as people read it: "cornflower" → "Cornflower". */
function colourName(colour: PaletteColour): string {
  return colour.charAt(0).toUpperCase() + colour.slice(1)
}

/**
 * The colour a new track starts with: the one fewest current tracks use, so a
 * list built up one track at a time spreads across the palette rather than
 * clustering. Shown selected from the start and saved with the track, so what
 * the editor shows is what the track gets.
 */
function leastUsedColour(tracks: readonly Track[]): PaletteColour {
  const counts = new Map<PaletteColour, number>(PALETTE.map((colour) => [colour, 0]))
  for (const track of tracks) {
    if (!track.archived) {
      const colour = colourOfTrack(track)
      counts.set(colour, (counts.get(colour) ?? 0) + 1)
    }
  }
  return PALETTE.reduce((best, colour) =>
    (counts.get(colour) ?? 0) < (counts.get(best) ?? 0) ? colour : best,
  )
}

/**
 * Creating and configuring a track (T-302).
 *
 * Everything that decides whether an edit is *safe* lives in core —
 * `planUpdate` reads the measurements and reports what would break, and
 * `applyEdit` refuses rather than trusting the form. This screen's job is to
 * ask before the user gets that far, so a blocked save is a rarity rather than
 * the normal way to discover a rule.
 */
export function TrackEditorScreen() {
  const { repos, ids } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const trackId = params['id']
  const isNew = trackId === undefined || trackId === 'new'

  const [loaded, setLoaded] = useState(isNew)
  const [existing, setExisting] = useState<Track | null>(null)
  const [measurementCount, setMeasurementCount] = useState(0)
  const [recordedOptionIds, setRecordedOptionIds] = useState<ReadonlySet<string>>(
    new Set(),
  )

  const [name, setName] = useState('')
  const [unit, setUnit] = useState('')
  const [description, setDescription] = useState('')
  const [question, setQuestion] = useState('')
  const [group, setGroup] = useState('')
  /*
   * The colour the user has chosen. For an existing track `undefined` means
   * "never chosen" and stays that way unless a swatch is tapped, so the track
   * keeps the colour its id gives it. A new track gets one up front.
   */
  const [colour, setColour] = useState<PaletteColour | undefined>(undefined)
  const [knownGroups, setKnownGroups] = useState<readonly string[]>([])
  const [plan, setPlan] = useState<RemovalPlan | null>(null)
  const [kind, setKind] = useState<TrackKind>(DEFAULT_NUMERIC)
  const [scale, setScale] = useState<ScaleConfig>(fromNumericKind(DEFAULT_NUMERIC))
  const [words, setWords] = useState<readonly ValueWord[]>([])
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)
  const [preview, setPreview] = useState<TrackValue | null>(null)

  // One flag for the update-prompt deferral, the indicator and the discard
  // confirmation (T-204, T-808).
  const guard = useDiscardGuard(dirty)

  // Every group in use, offered as suggestions so a second track in a group
  // is typed the same way as the first. A new track's starting colour comes
  // from the same read.
  useEffect(() => {
    let cancelled = false
    void repos.tracks.list({ includeArchived: true }).then((tracks) => {
      if (cancelled) {
        return
      }
      const groups = new Set(
        tracks.map((track) => track.group?.trim() ?? '').filter((name) => name !== ''),
      )
      setKnownGroups([...groups].sort((a, b) => a.localeCompare(b)))
      if (isNew) {
        setColour((current) => current ?? leastUsedColour(tracks))
      }
    })
    return () => {
      cancelled = true
    }
  }, [isNew, repos])

  useEffect(() => {
    if (isNew) {
      return
    }
    let cancelled = false
    async function load() {
      const track = await repos.tracks.get(trackId!)
      const entries = await repos.entries.listByTrack(trackId!)
      if (cancelled || !track) {
        return
      }
      const measurements = entries.flatMap((entry) =>
        entry.measurements.filter((m) => m.trackId === trackId),
      )
      setExisting(track)
      setName(track.name)
      setUnit(track.unit ?? '')
      setDescription(track.description ?? '')
      setQuestion(track.question ?? '')
      setGroup(track.group ?? '')
      setColour(track.colour)
      setKind(track.kind)
      if (track.kind.kind === 'numeric') {
        setScale(fromNumericKind(track.kind))
        setWords(track.kind.words ?? [])
      }
      setMeasurementCount(measurements.length)
      // Every option id any measurement points at, so none of them can be
      // deleted out from under history. Asked of core, so this form and
      // `planUpdate` cannot disagree about which options are in use.
      setRecordedOptionIds(new Set(measurements.flatMap((m) => chosenOptionIds(m.value))))
      setLoaded(true)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [isNew, trackId, repos])

  /**
   * The kind as currently configured, with the numeric scale folded back in.
   *
   * Words ride alongside the scale rather than inside `ScaleConfig`: the scale
   * is min/max/step/decimals and the derivation rules that connect them, and a
   * list of labels has nothing to do with any of that.
   */
  const currentKind: TrackKind =
    kind.kind === 'numeric'
      ? {
          ...toNumericKind(scale),
          // Empty means no words at all, not an empty list — the same rule the
          // model uses, so a track that never had any stays that way.
          ...(words.length === 0 ? {} : { words }),
        }
      : kind

  const previewTrack: Track = {
    id: existing?.id ?? 'preview',
    name: name.trim() || 'Untitled',
    ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
    ...(question.trim() === '' ? {} : { question: question.trim() }),
    ...(colour === undefined ? {} : { colour }),
    kind: currentKind,
    archived: false,
    position: existing?.position ?? 0,
    createdAt: existing?.createdAt ?? { epochMs: 0, timeZone: 'UTC' },
    updatedAt: existing?.updatedAt ?? { epochMs: 0, timeZone: 'UTC' },
  }

  /*
   * What an empty field will actually produce, asked of core rather than
   * rebuilt here. Passing a blank question is the same thing the model treats
   * as "no question", so the placeholder cannot drift from the derivation.
   */
  const derivedQuestion = questionFor({ ...previewTrack, question: '' })

  const changeKind = useCallback(
    (next: TrackKindName) => {
      setDirty(true)
      setPreview(null)
      if (next === 'numeric') {
        setScale(fromNumericKind(DEFAULT_NUMERIC))
        setKind(DEFAULT_NUMERIC)
      } else if (next === 'boolean') {
        setKind(DEFAULT_BOOLEAN)
      } else if (next === 'marker') {
        setKind({ kind: 'marker' })
      } else if (next === 'multi') {
        setKind({
          kind: 'multi',
          options: [
            { id: ids.next(), label: '', score: null },
            { id: ids.next(), label: '', score: null },
          ],
        })
      } else {
        setKind({
          kind: 'enum',
          ordered: false,
          options: [
            { id: ids.next(), label: '', score: null },
            { id: ids.next(), label: '', score: null },
          ],
        })
      }
    },
    [ids],
  )

  /*
   * Writes, and reports whether it worked — without navigating.
   *
   * Split out so the unsaved-changes sheet can offer Save: that sheet is
   * holding a navigation the user already chose, so the save must not pick a
   * destination of its own, and a failure has to leave them here with the
   * errors rather than leaving anyway.
   */
  async function persist(): Promise<boolean> {
    setErrors([])
    const changes = {
      name: name.trim(),
      kind: currentKind,
      ...(unit.trim() === '' ? { unit: undefined } : { unit: unit.trim() }),
      ...(description.trim() === ''
        ? { description: undefined }
        : { description: description.trim() }),
      // Cleared means "derive it again", not "ask an empty question", so this
      // sends `undefined` and the field goes away rather than holding ''.
      ...(question.trim() === ''
        ? { question: undefined }
        : { question: question.trim() }),
      ...(group.trim() === '' ? { group: undefined } : { group: group.trim() }),
      // Only ever set, never cleared: leaving the swatches alone keeps
      // whatever the track already had, chosen or derived.
      ...(colour === undefined ? {} : { colour }),
    }

    const result = isNew
      ? await repos.tracks.create({
          name: changes.name,
          kind: currentKind,
          ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
          ...(description.trim() === '' ? {} : { description: description.trim() }),
          ...(question.trim() === '' ? {} : { question: question.trim() }),
          ...(group.trim() === '' ? {} : { group: group.trim() }),
          ...(colour === undefined ? {} : { colour }),
        })
      : // applyEdit, not update: it re-checks the history guards server-side of
        // this form, so a stale page cannot slip a damaging change past them.
        await repos.tracks.applyEdit(existing!.id, changes)

    if (!result.ok) {
      setErrors(result.errors)
      return false
    }
    setDirty(false)
    // Leaving on purpose, so the guard stands aside. Without this it blocks the
    // app's own post-save navigation: `setDirty(false)` and `navigate()` run in
    // the same tick and the re-render has not happened yet.
    guard.allowLeaving()
    show({ message: isNew ? `Created ${result.value.name}.` : 'Saved.' })
    return true
  }

  async function save() {
    if (await persist()) {
      void navigate('/tracks')
    }
  }

  /** Asks core what removing would do, so the dialog can say so truthfully. */
  function planRemoval() {
    if (existing === null) {
      return
    }
    void repos.tracks.planRemoval(existing.id).then((result) => {
      if (result.ok) {
        setPlan(result.value)
      }
    })
  }

  async function confirmRemoval() {
    if (plan === null) {
      return
    }
    const { track } = plan
    setPlan(null)
    const result = await repos.tracks.remove(track.id)
    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'That did not work.', tone: 'danger' })
      return
    }
    setDirty(false)
    guard.allowLeaving()
    show({
      message:
        result.value.kind === 'delete'
          ? `Deleted ${track.name}.`
          : `Archived ${track.name}. Its history is kept.`,
    })
    void navigate('/tracks')
  }

  const back = { to: '/tracks', label: 'Tracks' }

  if (!loaded) {
    return (
      <Page>
        <SubHeader back={back} />
        <h1>Track</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  const typeLocked = measurementCount > 0
  const shownColour = colour ?? colourOfTrack(previewTrack)

  return (
    <Page>
      <SubHeader back={back} />
      <div className={styles['title']}>
        <Swatch colour={shownColour} size={14} />
        <h1>{isNew ? 'New track' : name || 'Track'}</h1>
      </div>
      <UnsavedWorkGuard
        guard={guard}
        what={isNew ? 'This new track' : 'This track'}
        onSave={persist}
      />

      {/*
        A starting point, on the screen where somebody is deciding what to
        measure. The same packs the empty home screen offers, but not gated on
        having said yes the first time — the moment you most want an example
        is the moment you are staring at a blank name field, which is usually
        not day one.

        Closed by default, because this screen is for defining a track and
        four packs above the name field are four things to scroll past.
      */}
      {isNew && (
        <details className={styles['packs']}>
          <summary className={styles['packsSummary']}>
            Or start from a ready-made set
          </summary>
          <p className={styles['packsNote']}>
            Tracks and check-ins for a few common things. They are ordinary tracks once
            added — rename, change or delete any of them.
          </p>
          <ExamplePackOffer mode="always" onInstalled={() => void navigate('/tracks')} />
        </details>
      )}

      <div className={styles['fields']}>
        <TextField
          label="Name"
          value={name}
          placeholder="What are you measuring?"
          onChange={(event) => {
            setName(event.target.value)
            setDirty(true)
          }}
        />
        {/*
          The derived default lives in the **placeholder**, not the value
          (T-810). Leaving this blank means "ask `${name}?`", and the
          placeholder shows exactly what that will be as the name is typed.

          The prototype copies the derived question into the field on create.
          That version has a bug it cannot see: rename the track and the
          question still asks the old name, so a track called Sleep spends the
          rest of its life asking about Kip. Deriving on read fixes it for
          free and makes "blank" a state rather than an omission.
        */}
        <TextField
          label="The question you'll be asked"
          optional
          value={question}
          placeholder={derivedQuestion}
          onChange={(event) => {
            setQuestion(event.target.value)
            setDirty(true)
          }}
        />
      </div>

      <section
        className={styles['kindSection']}
        data-bleed
        aria-labelledby="kind-heading"
      >
        <h2 id="kind-heading" className={styles['heading']}>
          What kind of value?
        </h2>
        <div className={styles['kinds']} role="group" aria-labelledby="kind-heading">
          {KIND_NAMES.map((option) => {
            const selected = option.id === currentKind.kind
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={selected}
                aria-label={option.label}
                aria-describedby={`kind-hint-${option.id}`}
                disabled={typeLocked && !selected}
                className={styles['kindOption']}
                onClick={() => changeKind(option.id)}
              >
                <span className={styles['radio']} aria-hidden="true" />
                <span className={styles['kindText']}>
                  <span className={styles['kindLabel']}>{option.label}</span>
                  <span id={`kind-hint-${option.id}`} className={styles['kindHint']}>
                    {option.hint}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        {/*
          Stated, not merely disabled. A control that silently does nothing
          reads as a bug; one that says why reads as a rule.
        */}
        {typeLocked && (
          <p className={styles['locked']}>
            This track already has{' '}
            {measurementCount === 1
              ? '1 measurement'
              : `${String(measurementCount)} measurements`}
            , so its value type is fixed — an existing measurement cannot be converted
            into a different kind of value without changing what was recorded. Create a
            new track if you need a different type.
          </p>
        )}
      </section>

      {matchTrackKind(currentKind, {
        numeric: () => (
          <>
            <div className={styles['block']}>
              <h2 className={styles['heading']}>Scale</h2>
              <NumericKindEditor
                scale={scale}
                onChange={(next) => {
                  setScale(next)
                  setDirty(true)
                }}
              />
              <TextField
                label="Unit"
                optional
                value={unit}
                placeholder="kg, hours, cups…"
                onChange={(event) => {
                  setUnit(event.target.value)
                  setDirty(true)
                }}
              />
            </div>
            <ValueWordsEditor
              scale={scale}
              words={words}
              colour={shownColour}
              onChange={(next) => {
                setWords(next)
                setDirty(true)
              }}
            />
          </>
        ),
        boolean: (booleanKind) => (
          <div className={styles['block']}>
            <h2 className={styles['heading']}>Words for each answer</h2>
            <div className={styles['pair']}>
              <TextField
                label="Label for yes"
                value={booleanKind.trueLabel}
                onChange={(event) => {
                  setKind({ ...booleanKind, trueLabel: event.target.value })
                  setDirty(true)
                }}
              />
              <TextField
                label="Label for no"
                value={booleanKind.falseLabel}
                onChange={(event) => {
                  setKind({ ...booleanKind, falseLabel: event.target.value })
                  setDirty(true)
                }}
              />
            </div>
          </div>
        ),
        enum: (enumKind: EnumKind) => (
          <div className={styles['block']}>
            <h2 className={styles['heading']}>Options</h2>
            <EnumKindEditor
              kind={enumKind}
              recordedOptionIds={recordedOptionIds}
              newOptionId={() => ids.next()}
              onChange={(next) => {
                setKind(next)
                setDirty(true)
              }}
            />
          </div>
        ),
        multi: (multiKind: MultiKind) => (
          <div className={styles['block']}>
            <h2 className={styles['heading']}>Options</h2>
            <MultiKindEditor
              kind={multiKind}
              recordedOptionIds={recordedOptionIds}
              newOptionId={() => ids.next()}
              onChange={(next) => {
                setKind(next)
                setDirty(true)
              }}
            />
          </div>
        ),
        /*
          Nothing to configure, so the section says what the type is for
          instead. An empty panel under a chosen kind reads as a form that
          failed to load.
        */
        marker: () => (
          <p className={styles['kindNote']}>
            Records only that it happened, and when. There is no value to choose and no
            “no” to record — a day with nothing logged is a day it did not happen.
          </p>
        ),
      })}

      <div className={styles['block']}>
        <h2 id="colour-heading" className={styles['heading']}>
          Colour
        </h2>
        <div className={styles['colours']} role="group" aria-labelledby="colour-heading">
          {PALETTE.map((option) => (
            <button
              key={option}
              type="button"
              aria-label={colourName(option)}
              aria-pressed={option === shownColour}
              className={styles['colourOption']}
              style={{ background: `var(--hue-${option}-fill)` }}
              onClick={() => {
                setColour(option)
                setDirty(true)
              }}
            />
          ))}
        </div>
        <p className={styles['note']}>
          {colourName(shownColour)} · used on charts, the timeline and reports
        </p>
      </div>

      <div className={styles['fields']}>
        <TextField
          label="Group"
          optional
          value={group}
          list="track-groups"
          placeholder="Lists it under a heading on Tracks"
          onChange={(event) => {
            setGroup(event.target.value)
            setDirty(true)
          }}
        />
        <datalist id="track-groups">
          {knownGroups.map((known) => (
            <option key={known} value={known} />
          ))}
        </datalist>
        <TextArea
          label="Description"
          optional
          value={description}
          placeholder="A note to your future self about what this means."
          onChange={(event) => {
            setDescription(event.target.value)
            setDirty(true)
          }}
        />
      </div>

      {/*
        The real control, not a picture of one. Seeing the thing you will tap
        dozens of times a week is how you notice a 200-position slider before
        you have logged against it for a month.
      */}
      <Section label="This is what logging will look like" padded>
        <ValueInput track={previewTrack} value={preview} onChange={setPreview} />
      </Section>

      {errors.length > 0 && (
        <div className={styles['errors']} role="alert">
          {errors.map((error) => (
            <span key={error.code} className={styles['error']}>
              {error.message}
            </span>
          ))}
        </div>
      )}

      {/*
        Removing goes through `planRemoval`, so the dialog says truthfully
        whether this deletes a track nobody has used or archives one with
        history (ADR-0003).
      */}
      {!isNew && (
        <button type="button" className={styles['archiveLink']} onClick={planRemoval}>
          {measurementCount > 0 ? 'Archive this track' : 'Delete this track'}
        </button>
      )}

      <ActionBar>
        <ActionButton onClick={() => void navigate('/tracks')}>Cancel</ActionButton>
        <ActionButton primary onClick={() => void save()}>
          {isNew ? 'Create track' : 'Save track'}
        </ActionButton>
      </ActionBar>

      <RemoveTrackDialog
        plan={plan}
        onCancel={() => setPlan(null)}
        onConfirm={() => void confirmRemoval()}
      />
    </Page>
  )
}
